/**
 * Scheduling rules for the Real Time board. Every threshold/policy the UI enforces lives here
 * so product can tune them in one place. Server-side enforcement should mirror these.
 */
import type { Job, JobWorker, WorkerJobStatus, WorkerResponse } from './types'

/**
 * Worker response / acceptance UI (Accepted / Pending / Declined / Expired chips, response pin
 * colours, response-styled timeline blocks, response toasts). Off for now: the board only shows
 * who is assigned. Flip to true to bring the response UI back; the data is still tracked.
 */
export const SHOW_WORKER_RESPONSE_STATUS = false

/** Nearby-worker suggestions for the selected job ("Nearby workers" strip + blue map pins). */
export const NEARBY_RADIUS_KM = 10
/** "Increase radius" step in the empty state. */
export const NEARBY_RADIUS_STEP_KM = 5
/** Upper bound for "Increase radius". */
export const NEARBY_MAX_RADIUS_KM = 50
/**
 * false: only workers whose live status is `available` (not on a job, not late, not offline,
 * not on leave). true: also include workers currently on a job (they get a Conflict chip when
 * their schedule overlaps).
 */
export const INCLUDE_BUSY_WORKERS = false
/** Candidates shown before "Show more". */
export const MAX_NEARBY_CANDIDATES = 12
/** Nearest candidates included in the selection fitBounds. */
export const FIT_NEAREST_CANDIDATES = 3
/** Straight-line ETA fallback when Distance Matrix is off or fails. */
export const ETA_MINUTES_PER_KM = 3
/** Candidate distances are recomputed from live locations at most this often. */
export const CANDIDATE_RECOMPUTE_MS = 3_000

export const schedulingRules = {
  /** Pending offers show an amber "No response Nm" hint after this long (response UI only). */
  noResponseHintAfterMinutes: 15,
  /** Pending offers expire after this long. Expiry is server-side; see clientSideExpiry. */
  expireAfterMinutes: 30,
  /**
   * When true the client also displays stale pending offers as expired. Off by default: the
   * server owns expiry and the client only displays it (the current backend does not expire
   * offers, so turning this on would mislabel genuinely pending ones).
   */
  clientSideExpiry: false,
  /** With the response UI on, removing a worker in these states asks for a reason. */
  reasonRequiredForResponses: ['accepted'] as WorkerResponse[],
  reasonRequiredForStatuses: ['on_the_way', 'checked_in', 'started'] as WorkerJobStatus[],
  removalReasons: ['Sick', 'Unavailable', 'Customer request', 'Other'] as const,
  /** Permission code that allows removing workers after the job has started. */
  forceRemovePermission: 'jobs.force_remove_worker',
  /** Map tags (job/worker labels) are hidden below this zoom. */
  mapTagMinZoom: 12,
  /** Jobs panel width (px) on desktop (200-240). */
  panelWidthPx: 232,
  /** Compact job card: worker rows shown before "+N more". */
  maxVisibleWorkerRows: 3,
  /** Map popup (desktop only) width and its offset from the map's top-left corner. */
  popupWidthPx: 210,
  popupInsetPx: 12,
  /** fitBounds padding while a job is selected (bottom leaves room for the nearby strip). */
  selectionFitPadding: { top: 80, right: 60, bottom: 120, left: 60 },
  /** fitBounds padding for the overview (no selection). */
  fitPaddingPx: 80,
  selectionMaxZoom: 15,
  fitDebounceMs: 150,
  /** How long a changed worker row flashes. */
  rowFlashMs: 1_500,
  /** Mobile bottom sheet snap points as a fraction of the viewport height. */
  mobileSheetSnaps: { peek: 0.3, half: 0.55, full: 0.9 },
} as const

export type RemovalReason = (typeof schedulingRules.removalReasons)[number]

const MINUTE_MS = 60_000

const minutesSince = (iso: string | null | undefined, now: number) => {
  const at = Date.parse(String(iso ?? ''))
  return Number.isFinite(at) ? (now - at) / MINUTE_MS : null
}

/** The response to display; with clientSideExpiry, stale pending offers show as expired. */
export function getEffectiveResponse(
  jobWorker: JobWorker,
  now: number,
  { clientSideExpiry = schedulingRules.clientSideExpiry }: { clientSideExpiry?: boolean } = {},
): WorkerResponse {
  if (jobWorker.response !== 'pending' || !clientSideExpiry) return jobWorker.response
  const age = minutesSince(jobWorker.sentAt, now)
  return age != null && age >= schedulingRules.expireAfterMinutes ? 'expired' : 'pending'
}

export function isJobStarted(job: Job): boolean {
  return job.status === 'in_progress' || job.workers.some((worker) => worker.status === 'started' || worker.status === 'completed')
}

export interface RemovalRule {
  allowed: boolean
  /** Stronger warning + mandatory reason (only with SHOW_WORKER_RESPONSE_STATUS). */
  requiresReason: boolean
  blockedReason: string | null
}

export function getRemovalRule(job: Job, jobWorker: JobWorker, { canForceRemove }: { canForceRemove: boolean }): RemovalRule {
  if (isJobStarted(job) && !canForceRemove) {
    return { allowed: false, requiresReason: true, blockedReason: 'Cannot remove after job started' }
  }
  const requiresReason = SHOW_WORKER_RESPONSE_STATUS && (
    schedulingRules.reasonRequiredForResponses.includes(jobWorker.response)
    || schedulingRules.reasonRequiredForStatuses.includes(jobWorker.status)
    || isJobStarted(job)
  )
  return { allowed: true, requiresReason, blockedReason: null }
}

/**
 * Reads a permission from the active post: role permission map first, then capability flags.
 * Accepts dotted codes ("jobs.force_remove_worker") and their snake_case variant.
 */
export function hasPostPermission(
  post: { roles_data?: { permissions?: Record<string, unknown> | null } | null; capabilities?: Record<string, unknown> | null } | null | undefined,
  code: string,
): boolean {
  if (!post) return false
  const variants = [code, code.replace(/\./g, '_')]
  const sources = [post.roles_data?.permissions, post.capabilities]
  return sources.some((source) => source && variants.some((key) => {
    const value = (source as Record<string, unknown>)[key]
    return value === true || value === 1 || value === 'true' || value === '1'
  }))
}

export interface FitPadding {
  top: number
  right: number
  bottom: number
  left: number
}

/**
 * fitBounds padding so pins are never hidden behind UI:
 * - desktop, job selected: `selectionFitPadding` (bottom 120 for the nearby strip); when the map
 *   popup is expanded the left side also clears the popup (it sits over the map's top-left)
 * - desktop, overview: `fitPaddingPx` on every side
 * - mobile: bottom = the part of the bottom sheet that overlaps the map (no popup on mobile)
 */
export function computeFitPadding({
  isMobile,
  hasSelection = false,
  popupExpanded = false,
  sheetOverlapPx = 0,
  base = schedulingRules.fitPaddingPx,
}: {
  isMobile: boolean
  hasSelection?: boolean
  popupExpanded?: boolean
  sheetOverlapPx?: number
  base?: number
}): FitPadding {
  if (isMobile) {
    const side = Math.round(base / 2)
    return { top: base, right: side, bottom: Math.max(Math.round(sheetOverlapPx), side), left: side }
  }
  if (!hasSelection) return { top: base, right: base, bottom: base, left: base }
  const padding: FitPadding = { ...schedulingRules.selectionFitPadding }
  if (popupExpanded) {
    padding.left = Math.max(padding.left, schedulingRules.popupWidthPx + schedulingRules.popupInsetPx + 24)
  }
  return padding
}
